import MicroModal from 'micromodal';
import cookies from 'js-cookie';

import UiElement from '../../../shared/ui-element/ui-element';
import WebAssistantInput from '../web-assistant-input/web-assistant-input';
import Logger from '../../utils/logger/logger';
import { getCID } from '../../utils/installer-url/installer-url';
import analytics, { events } from '../../../shared/analytics';

import './web-assistant-modal.scss';

const REFRESH_TOKEN_NAME = 'customer_refresh_token';

export default class WebAssistantModal extends UiElement {
  modalIdSelector = '#web-assistant-modal';
  doneStreamingClass = 'web-assistant-modal__streaming-done';
  rateButtonActiveClass = 'web-assistant-modal__rate-btn_active';

  // appNamesRegex is regex string like 'CleanMyMac|Gemini|Wallpaper Wizard'
  appNamesRegex: string | null = null;

  ui = {
    modal: this.modalIdSelector,
    modalCloseBtn: '#web-assistant-modal .modal__close',
    modalOverlay: '#web-assistant-modal .modal__overlay',
    messagesContainer: '.js-messages-container',
    botMessageTemplate: '.js-bot-message-template',
    userMessageTemplate: '.js-user-message-template',
  };

  async onInit() {
    super.onInit();

    const input = new WebAssistantInput(this.modalIdSelector, this.handleInputSubmit.bind(this));
    input.init();

    fetch('/ajax/application-names').then((res) => res.json()).then((json) => {
      this.appNamesRegex = json['data'];
    });
  }

  open(options?: { value: string | undefined }) {
    MicroModal.show('web-assistant-modal', {
      closeTrigger: 'data-base-modal-close',
      disableScroll: true,
      onClose: () => analytics.trackEvent(events.WEB_ASSISTANT_CLOSE),
    });

    if (options?.value) {
      this.addUserMessage(options.value);
      this.processUserMessage(options.value);
    }
  }

  handleInputSubmit(value: string) {
    this.addUserMessage(value);
    this.processUserMessage(value);
  }

  addUserMessage(message: string) {
    const messagesContainer = this.ui.messagesContainer as unknown as HTMLDivElement;

    const template = this.ui.userMessageTemplate as unknown as HTMLDivElement;
    const clone = template.cloneNode(true) as HTMLDivElement;
    clone.classList.remove('d-none');

    const messageElem = clone.querySelector('.js-message-content') as HTMLParagraphElement;
    messageElem.textContent = message;

    messagesContainer.appendChild(clone);
  }

  async processUserMessage(message: string) {
    const response = fetch('/api/web-assistant', {
      method: 'POST',
      body: JSON.stringify({ input: message }),
      headers: { 'Content-Type': 'application/json' },
    });

    this.streamTextFromResponse(response, message);
  }

  createBotMessageElement(): HTMLDivElement {
    const messagesContainer = this.ui.messagesContainer as unknown as HTMLDivElement;

    const template = this.ui.botMessageTemplate as unknown as HTMLDivElement;
    const clone = template.cloneNode(true) as HTMLDivElement;
    clone.classList.remove('d-none');

    const likeBtn = clone.querySelector('.js-like-btn') as HTMLButtonElement;
    const dislikeBtn = clone.querySelector('.js-dislike-btn') as HTMLButtonElement;

    clone.querySelector('.js-like-btn')?.addEventListener('click', () => {
      likeBtn.classList.add(this.rateButtonActiveClass);
      dislikeBtn.classList.remove(this.rateButtonActiveClass);

      analytics.trackEvent({
        ...events.WEB_ASSISTANT_RATE_RESPONSE,
        eventLabel: 'Like',
      });
    });

    clone.querySelector('.js-dislike-btn')?.addEventListener('click', () => {
      dislikeBtn.classList.add(this.rateButtonActiveClass);
      likeBtn.classList.remove(this.rateButtonActiveClass);

      analytics.trackEvent({
        ...events.WEB_ASSISTANT_RATE_RESPONSE,
        eventLabel: 'Dislike',
      });
    });

    messagesContainer.appendChild(clone);

    this.scrollToEnd();

    return clone;
  }

  async getAppRecommendations(
    botMessageElem: HTMLDivElement,
    appRecommendationsBlock: HTMLParagraphElement
  ): Promise<{ recommendedApps: string[]; recommendedAppIds: string[] }> {
    const botMessageContent = botMessageElem.querySelector('.js-message-content') as HTMLParagraphElement;

    const recommendedApps: string[] = [];
    const recommendedAppIds: string[] = [];

    if (!this.appNamesRegex) {
      return { recommendedApps, recommendedAppIds };
    }

    const regex = new RegExp(this.appNamesRegex, 'g');
    const appNameMatches = new Set(Array.from(botMessageContent.textContent!.matchAll(regex)).map((match) => match[0]));

    const appRecommendationsUrl = new URLSearchParams();

    let index = 0;
    appNameMatches.forEach((appName) => {
      appRecommendationsUrl.set(`q[${index}]`, appName);
      index++;
    });

    if (appNameMatches.size === 0) {
      return { recommendedApps, recommendedAppIds };
    }

    try {
      const response = await fetch(`/ajax/application-previews?${appRecommendationsUrl.toString()}`);

      if (!response.ok) {
        throw new Error(`Failed to get app recommendations for web assistant: ${response.statusText}`);
      }

      const appRecommendations = await response.json();
      const appData = appRecommendations['data'];

      let textWithLinks = botMessageContent.textContent!;

      appData.forEach((app: { name: string; url: string; id: string }) => {
        recommendedApps.push(app.name);
        recommendedAppIds.push(app.id);

        textWithLinks = textWithLinks.replaceAll(app.name, (match) => {
          return `<a
            class="web-assistant-modal__link"
            href="${app.url}"
            target="_blank"
            rel="noopener noreferrer"
            data-event-type="Link"
          >${match}</a>`;
        });
      });

      botMessageContent.innerHTML = textWithLinks;

      appRecommendationsBlock.innerHTML = appRecommendations['view'];

      botMessageElem.querySelectorAll('[data-event-type]').forEach((elem) => {
        elem.addEventListener('click', () => {
          analytics.trackEvent({
            ...events.WEB_ASSISTANT_CLICK_RECOMMENDED,
            eventLabel: (elem as HTMLElement).dataset.eventType,
          });
        });
      });
    } catch (e) {
      Logger.catchError(e);
    }

    return { recommendedApps, recommendedAppIds };
  }

  async streamTextFromResponse(response: Promise<Response>, userQuery: string) {
    const botMessageElem = this.createBotMessageElement();

    const startTime = Date.now();

    const messagesContainer = this.ui.messagesContainer as unknown as HTMLDivElement;
    const botMessageContent = botMessageElem.querySelector('.js-message-content') as HTMLParagraphElement;
    const appRecommendationsBlock = botMessageElem.querySelector('.js-app-recommendations') as HTMLDivElement;

    const textDecoder = new TextDecoder();
    const responseResult = await response;

    if (!responseResult.ok) {
      const errors = (await responseResult.json())['errors'];
      const errorMessage = errors['input'] || 'Sorry, something went wrong. Please try again later.';

      botMessageContent.textContent = errorMessage;

      return;
    }

    const reader = responseResult.body?.getReader();

    let newlineAtStartTrimmed = false;
    let userHasScrolled = false;

    const scrollListener = () => (userHasScrolled = true);

    messagesContainer.addEventListener('wheel', scrollListener);

    const processText = async ({ done, value }: ReadableStreamReadResult<Uint8Array>) => {
      if (done) {
        botMessageContent.classList.add(this.doneStreamingClass);

        this.scrollToEnd();

        const { recommendedAppIds, recommendedApps }
          = await this.getAppRecommendations(botMessageElem, appRecommendationsBlock);

        this.scrollToEnd();

        analytics.trackEvent(events.WEB_ASSISTANT_RESPONSE_READY);

        const responseDuration = Date.now() - startTime;

        this.sendAnalyticsToDatahub({
          query: userQuery,
          appsList: recommendedApps,
          appsListIds: recommendedAppIds,
          response: botMessageContent.textContent,
          responseDuration,
        });

        return undefined;
      }

      botMessageContent.textContent += textDecoder.decode(value);

      if (!newlineAtStartTrimmed) {
        botMessageContent.textContent = botMessageContent.textContent!.trimStart();
        newlineAtStartTrimmed = true;
      }

      if (botMessageContent.getBoundingClientRect().bottom
          > messagesContainer.getBoundingClientRect().bottom - 10
          && !userHasScrolled
      ) {
        this.scrollToEnd();
      }

      return reader?.read().then(processText);
    };

    reader?.read().then(processText)
      .catch((err: Error) => Logger.catchError(err))
      .finally(() => messagesContainer.removeEventListener('wheel', scrollListener));
  }

  scrollToEnd() {
    const messagesContainer = this.ui.messagesContainer as unknown as HTMLDivElement;

    messagesContainer.scrollTo({
      top: messagesContainer.scrollHeight,
      behavior: 'smooth',
    });
  }

  sendAnalyticsToDatahub({ query, response, responseDuration, appsList, appsListIds }) {
    const refreshToken = cookies.get(REFRESH_TOKEN_NAME);

    const userId = refreshToken ? refreshToken.split('|')[0] : null;

    try {
      analytics.insertDatahubData('assistant_queries', {
        cid: getCID(),
        source: 'web_assistant',
        platform: 'web',
        query,
        response,
        'response_duration': responseDuration,
        'apps_list': appsList,
        'apps_list_ids': appsListIds,
        ...(Boolean(userId) && { 'setapp_uid': userId }),
      });
    } catch (e) {
      Logger.catchError(e);
    }
  }
}
